import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent } from "@mui/material";
import { resetGetVerificationSelfieQuery } from "@src/appV2/Agents/api/useGetVerificationSelfie";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { BottomSheet, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { StripeIdentityVerificationFlowStatus } from "@src/appV2/Stripe";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { DOCUMENT_STRIPE_VERIFICATION_IN_PROGRESS_FULL_PATH } from "../../../paths";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";
import { PersonalIdSubtypeSelector } from "./PersonalIdSubtypeSelector";
import { useStripeIdentityVerificationFlow } from "./useStripeIdentityVerificationFlow";

interface DocumentDetailsStripeVerificationSelectSubtypeBottomSheetProps {
  modalState: UseModalState;
}

export function DocumentDetailsStripeVerificationSelectSubtypeBottomSheet(
  props: DocumentDetailsStripeVerificationSelectSubtypeBottomSheetProps
) {
  const history = useHistory();
  const { modalState } = props;
  const queryClient = useQueryClient();
  const {
    requirement: { requirementId },
  } = useRequirementDetailsContext();

  const [selectedPersonalIdSubtype, setSelectedPersonalIdSubtype] = useState<string | undefined>(
    undefined
  );

  const { startStripeVerificationFlow, isLoading } = useStripeIdentityVerificationFlow({
    requirementId,
  });

  const handleUploadButtonClick = async () => {
    if (isDefined(selectedPersonalIdSubtype)) {
      const { stripeVerificationStatus } = await startStripeVerificationFlow({
        personalIdSubtype: selectedPersonalIdSubtype,
      });

      if (stripeVerificationStatus === StripeIdentityVerificationFlowStatus.COMPLETED) {
        resetGetVerificationSelfieQuery(queryClient);
        history.replace(
          generatePath(DOCUMENT_STRIPE_VERIFICATION_IN_PROGRESS_FULL_PATH, {
            requirementId,
          })
        );
      } else {
        modalState.closeModal();
      }
    }
  };

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
      onClose={handleClose}
    >
      <DialogContent>
        <PersonalIdSubtypeSelector
          selectedSubtype={selectedPersonalIdSubtype}
          isLoading={isLoading}
          title="Please select a document type"
          description={
            <Text
              semibold
              variant="body1"
              sx={{
                color: (theme) => theme.palette.text.secondary,
                textAlign: "center",
              }}
            >
              Stripe will ask for a selfie to verify your Personal ID. This selfie may be used to
              generate your badge, so take a well-lit photo with your full face in view.
            </Text>
          }
          onSubtypeChange={setSelectedPersonalIdSubtype}
          onContinue={handleUploadButtonClick}
        />
      </DialogContent>
    </BottomSheet>
  );
}
